import { TTalentForm } from "@/features/talent/types/talentForm";
import { background, divider, state, text } from "@/theme/colors";
import { containsNonNumeric } from "@/utils/input";
import { toNumber } from "@/utils/number";
import { convertFullWidthToHalfWidth } from "@/utils/pieces";
import { InputBase, Stack, Typography } from "@mui/material";
import { FC, useEffect, useState } from "react";
import { Path, useFormContext } from "react-hook-form";

type TYearMonthAmountInputProps = {
  readonly: boolean;
  isError: boolean;
  nameYear: Path<TTalentForm>;
  nameMonth: Path<TTalentForm>;
};

export const YearMonthAmountInput: FC<TYearMonthAmountInputProps> = ({ readonly, isError, nameYear, nameMonth }) => {
  const { getValues, setValue, trigger } = useFormContext<TTalentForm>();

  const formYear = getValues(nameYear) ?? "";
  const formMonth = getValues(nameMonth) ?? "";

  const [year, setYear] = useState<string>(formYear.toString());
  const [month, setMonth] = useState<string>(formMonth.toString());

  const [isComposingYear, setIsComposingYear] = useState(false);
  const [isComposingMonth, setIsComposingMonth] = useState(false);

  useEffect(() => {
    setYear(formYear.toString());
  }, [formYear]);

  useEffect(() => {
    setMonth(formMonth.toString());
  }, [formMonth]);

  const updateFormYear = (val: string) => {
    if (!containsNonNumeric(val)) {
      const num = toNumber(val);
      setValue(nameYear, num, { shouldValidate: true });
      trigger(nameYear);
    }
  };

  const updateFormMonth = (val: string) => {
    if (!containsNonNumeric(val)) {
      const num = toNumber(val);
      setValue(nameMonth, num, { shouldValidate: true });
      trigger(nameMonth);
    }
  };

  const onChangeYear: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const val = convertFullWidthToHalfWidth(e.target.value);
    if (containsNonNumeric(val)) return;

    setYear(val);

    if (!isComposingYear) {
      updateFormYear(val);
    }
  };

  const onCompositionStartYear = () => setIsComposingYear(true);

  const onCompositionEndYear = (e: React.CompositionEvent<HTMLInputElement>) => {
    setIsComposingYear(false);
    const val = convertFullWidthToHalfWidth(e.currentTarget.value);
    updateFormYear(val);
  };

  const onBlurYear = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsComposingYear(false);
    const val = convertFullWidthToHalfWidth(e.target.value);
    updateFormYear(val);
  };

  const onKeyUpYear = () => {
    setIsComposingYear(false);
  };

  const onChangeMonth: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const val = convertFullWidthToHalfWidth(e.target.value);
    if (containsNonNumeric(val)) return;

    setMonth(val);

    if (!isComposingMonth) {
      updateFormMonth(val);
    }
  };

  const onCompositionStartMonth = () => setIsComposingMonth(true);

  const onCompositionEndMonth = (e: React.CompositionEvent<HTMLInputElement>) => {
    setIsComposingMonth(false);
    const val = convertFullWidthToHalfWidth(e.currentTarget.value);
    updateFormMonth(val);
  };

  const onBlurMonth = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsComposingMonth(false);
    const val = convertFullWidthToHalfWidth(e.target.value);
    updateFormMonth(val);
  };

  const onKeyUpMonth = () => {
    setIsComposingMonth(false);
  };

  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      border={`1px solid ${isError ? state.error_1 : divider.middle}`}
      bgcolor={readonly ? background.disable : isError ? "#FCDFE4" : undefined}
      borderRadius={1}
      sx={{ pointerEvents: readonly ? "none" : undefined }}
      px={1}
    >
      <Stack direction="row" gap={1} alignItems="center">
        <InputBase
          sx={{
            width: "40px",
            flexShrink: 0,
            fontSize: "14px",
          }}
          value={year}
          onChange={onChangeYear}
          onCompositionStart={onCompositionStartYear}
          onCompositionEnd={onCompositionEndYear}
          onBlur={onBlurYear}
          onKeyUp={onKeyUpYear}
          placeholder="0"
          readOnly={readonly}
        />
        <Typography variant="body14" color={text.primary} sx={{ flexShrink: 0 }}>
          年
        </Typography>
      </Stack>

      <Stack direction="row" gap={1} alignItems="center">
        <InputBase
          sx={{
            width: "40px",
            flexShrink: 0,
            fontSize: "14px",
          }}
          value={month}
          onChange={onChangeMonth}
          onCompositionStart={onCompositionStartMonth}
          onCompositionEnd={onCompositionEndMonth}
          onBlur={onBlurMonth}
          onKeyUp={onKeyUpMonth}
          placeholder="0"
          readOnly={readonly}
        />
        <Typography variant="body14" color={text.primary} sx={{ flexShrink: 0 }}>
          ヶ月
        </Typography>
      </Stack>
    </Stack>
  );
};
